import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  PlusOutlined,
  GithubOutlined,
  FileOutlined,
  FileTextOutlined,
  PaperClipOutlined,
  LinkOutlined,
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Button, Tag, Space, Input, Modal, message, Upload, Tooltip, Spin } from "antd";

// Constant color palette – never changes
const SOFT_COLORS = ["#dbeafe", "#fef3c7", "#fee2e2", "#dcfce7", "#ede9fe"];

// Stable color selector based on a seed (id or name)
const getColorForItem = (seed) => {
  if (!seed) return SOFT_COLORS[0];
  const index = Math.abs(seed.toString().length) % SOFT_COLORS.length;
  return SOFT_COLORS[index];
};

const AttachmentsSection = React.memo(({
  links = [],
  files = [],
  onAddLink,
  onAddFile,
  onDelete,
  onFileUpload,
}) => {
  // AttachmentsSection render

  const [showAddForm, setShowAddForm] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Memoized combined list of attachments with stable colors
  const attachments = useMemo(() => {
    const linkItems = links.map((att) => ({
      ...att,
      type: "link",
      color: getColorForItem(att.id || att.fileName || att.name || att.url),
    }));
    const fileItems = files.map((file) => ({
      ...file,
      type: "file",
      color: getColorForItem(file.id || file.fileName || file.name),
    }));
    return [...linkItems, ...fileItems];
  }, [links, files]);

  const handleAddLink = (linkData) => {
    if (onAddLink) onAddLink(linkData);
    setShowAddForm(false);
  };

  const handleFileUpload = async (file) => {
    try {
      setUploading(true);

      let fileData;
      if (onFileUpload) {
        const result = await onFileUpload(file);

        fileData = {
          id: result.id || result.fileId || Date.now(),
          name: result.fileName || file.name,
          url: result.url || result.meta?.url || URL.createObjectURL(file),
          size: result.fileSize || file.size,
          type: file.type,
          fileId: result.fileId || result.id,
        };
      } else {
        // Fallback for local testing
        fileData = {
          id: Date.now(),
          name: file.name,
          url: URL.createObjectURL(file),
          size: file.size,
          type: file.type,
        };
      }

      if (onAddFile) {
        onAddFile(fileData);
      }

      message.success(`${file.name} uploaded successfully`);
      setShowAddForm(false);
    } catch (error) {
      console.error('Upload error:', error);
      message.error(`Failed to upload ${file.name}`);
      throw error;
    } finally {
      setUploading(false);
    }
    return false;
  };


  const handleDelete = async (id, type, opts = {}) => {
    try {
      if (onDelete) {
        await Promise.resolve(onDelete(id, type, opts));
        message.success("Attachment deleted");
      }
    } catch (err) {
      console.error("Failed to delete attachment", err);
      message.error("Failed to delete attachment");
    }
  };

  const handlePreview = (file) => {
    // Use the attachment's provided URL (usually `fileUrl`) immediately
    // to avoid any delay — backend returns a signed `fileUrl` we can use.
    setPreviewFile(file);
    setPreviewVisible(true);
  };

  const handleClosePreview = () => {
    setPreviewVisible(false);
    setPreviewFile(null);
  };

  const isImage = useCallback((file) => {
    if (!file) return false;
    return (
      file.type?.startsWith("image/") ||
      file.name?.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i) ||
      file.url?.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i)
    );
  }, []);

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Header */}
      <Space align="center" style={{ color: "#6b7280", fontSize: 13, marginBottom: 8 }}>
        <PaperClipOutlined />
        <span>Attachments ({attachments.length})</span>
      </Space>

      {/* Attachments List */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, minHeight: 40 }}>
        {attachments.length === 0 && !showAddForm && (
          <div style={{ color: "#9ca3af", fontSize: 12, padding: "4px 0" }}>
            No attachments yet
          </div>
        )}

        {attachments.map((att) => (
          <AttachmentPill
            key={att.id}
            attachment={att}
            onDelete={handleDelete}
            onPreview={handlePreview}
          />
        ))}

        {!showAddForm && (
          <Button
            icon={<PlusOutlined />}
            size="small"
            type="dashed"
            onClick={() => setShowAddForm(true)}
            style={{ borderRadius: 4 }}
          >
            Add
          </Button>
        )}
      </div>

      {/* Add Form Modal */}
      <Modal
        title="Add Attachment"
        open={showAddForm}
        onCancel={() => setShowAddForm(false)}
        footer={null}
        width={400}
        destroyOnClose
      >
        <AddAttachmentForm
          onAddLink={handleAddLink}
          onFileUpload={handleFileUpload}
          onCancel={() => setShowAddForm(false)}
          uploading={uploading}
        />
      </Modal>

      {/* Preview Modal */}
      <Modal
        title={previewFile?.name || previewFile?.label || "Preview"}
        open={previewVisible}
        onCancel={handleClosePreview}
        footer={null}
        width={isImage(previewFile) ? 800 : 500}
        destroyOnClose
      >
        {previewFile && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            {isImage(previewFile) ? (
              <img
                src={previewFile.url}
                alt={previewFile.name || previewFile.label}
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                  borderRadius: 4,
                }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://via.placeholder.com/400x300?text=Image+not+available";
                }}
              />
            ) : previewFile.type === "link" ? (
              <div style={{ padding: 20 }}>
                <LinkOutlined style={{ fontSize: 48, color: "#3b82f6" }} />
                <h3 style={{ margin: "16px 0" }}>{previewFile.label}</h3>
                <a
                  href={previewFile.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#3b82f6", wordBreak: "break-all" }}
                >
                  {previewFile.url}
                </a>
              </div>
            ) : (
              <div style={{ padding: 20 }}>
                <FileOutlined style={{ fontSize: 48, color: "#6b7280" }} />
                <h3 style={{ margin: "16px 0" }}>{previewFile.name}</h3>
                {previewFile.size && (
                  <p style={{ color: "#6b7280", marginBottom: 16 }}>
                    Size: {(previewFile.size / 1024).toFixed(2)} KB
                  </p>
                )}
                <Button
                  type="primary"
                  href={previewFile.url}
                  target="_blank"
                  icon={<EyeOutlined />}
                >
                  Open File
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
});

// Helper to choose an icon based on attachment type
const getAttachmentIcon = (attachment) => {
  if (!attachment) return <LinkOutlined />;
  if (attachment.type === "file") {
    const fileType = attachment.type || attachment.mimeType || "";
    const fileNameForExt = attachment.fileName || attachment.name || "";
    if (fileType?.startsWith("image/")) return <FileOutlined />;
    if (fileNameForExt?.match(/\.(pdf)$/i)) return <FileTextOutlined />;
    if (fileNameForExt?.match(/\.(doc|docx)$/i)) return <FileTextOutlined />;
    if (fileNameForExt?.match(/\.(xls|xlsx)$/i)) return <FileTextOutlined />;
    if (fileNameForExt?.match(/\.(txt|md)$/i)) return <FileTextOutlined />;
    return <FileOutlined />;
  }
  // For links
  const url = attachment.url?.toLowerCase() || "";
  if (url.includes("github")) return <GithubOutlined />;
  if (url.includes("docs.google")) return <FileTextOutlined />;
  if (url.includes("drive.google")) return <FileTextOutlined />;
  return <LinkOutlined />;
};

// Add Attachment Form (Link / File)
const AddAttachmentForm = ({ onAddLink, onFileUpload, onCancel, uploading }) => {
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [activeTab, setActiveTab] = useState("link");

  const handleAddLink = () => {
    if (!label || !url) {
      message.warning("Please fill in both fields");
      return;
    }
    try {
      new URL(url);
    } catch {
      message.warning("Please enter a valid URL");
      return;
    }
    if (onAddLink) {
      onAddLink({ id: Date.now(), label, url, type: "link" });
      message.success("Link added");
    }
    setLabel("");
    setUrl("");
  };

  const handleFileUpload = async (file) => {
    if (file.size > 10 * 1024 * 1024) {
      message.error("File size must be less than 10MB");
      return false;
    }
    return await onFileUpload(file);
  };

  const uploadProps = {
    beforeUpload: handleFileUpload,
    showUploadList: false,
    accept: ".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.webp,.xls,.xlsx",
    disabled: uploading,
  };

  return (
    <div>
      <Space style={{ marginBottom: 16, width: "100%" }} orientation="horizontal">
        <Button
          type={activeTab === "link" ? "primary" : "default"}
          onClick={() => setActiveTab("link")}
          icon={<LinkOutlined />}
        >
          Link
        </Button>
        <Button
          type={activeTab === "file" ? "primary" : "default"}
          onClick={() => setActiveTab("file")}
          icon={<FileOutlined />}
        >
          File
        </Button>
      </Space>

      {activeTab === "link" ? (
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Input
            placeholder="Label (e.g., Documentation)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            prefix={<FileTextOutlined />}
          />
          <Input
            placeholder="URL (e.g., https://example.com)"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            prefix={<LinkOutlined />}
          />
          <Space>
            <Button type="primary" onClick={handleAddLink}>
              Add Link
            </Button>
            <Button onClick={onCancel}>Cancel</Button>
          </Space>
        </Space>
      ) : (
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Upload.Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text" style={{ fontSize: 13, marginBottom: 6 }}>
              Click or drag files here to upload
            </p>
            <p className="ant-upload-hint" style={{ fontSize: 12, color: '#6b7280' }}>
              Supported: PDF, DOC, DOCX, XLS, XLSX, TXT, Images — Max 10MB
            </p>
          </Upload.Dragger>
          {uploading && (
            <div style={{ textAlign: "center" }}>
              <Spin size="small" /> Uploading...
            </div>
          )}
          <Button onClick={onCancel}>Cancel</Button>
        </Space>
      )}
    </div>
  );
};

// Individual attachment pill with preview and delete actions
const AttachmentPill = ({ attachment, onDelete, onPreview }) => {
  if (!attachment) return null;

  return (
    <Tag
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 8px",
        backgroundColor: attachment.color,
        fontSize: 11,
        fontFamily: "Inter, sans-serif",
        border: "none",
        margin: 0,
        cursor: "pointer",
      }}
      closable={false}
      onClick={() => onPreview(attachment)}
    >
      {getAttachmentIcon(attachment)}

      <span
        style={{
          maxWidth: 150,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {attachment.label || attachment.name}
      </span>

      <Space size={2} onClick={(e) => e.stopPropagation()}>
        <Tooltip title="Preview">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => onPreview(attachment)}
            style={{ padding: 2 }}
          />
        </Tooltip>
        <Tooltip title="Delete">
          <Button
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => onDelete && onDelete(attachment.id, attachment.type, { fileId: attachment.fileId })}
            style={{ padding: 2 }}
            danger
          />
        </Tooltip>
      </Space>
    </Tag>
  );
};

export default AttachmentsSection;